import { useUser } from '@innovamat/ga-features';
import { initDatadog, storage } from '@innovamat/radiance-utils';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import UnityWebGL from '../../components/UnityWebGL';
import { useUnityEvents } from '../../hooks/use-unity-events';
import { useUrls } from '../../hooks/use-urls';
import { useUnityDataStore } from '../../store/unity-data';
import type { BuildData } from './types';
import { BUILD_NOT_FOUND, buildResolver } from './utils';
import { useVerifyStudentAtlasPasswordMutation } from '@innovamat/glow-api-client';

const clientId = import.meta.env.VITE_USERS_CLIENT_ID_WEBAPP;

function Home(): JSX.Element {
  const { isLoggedIn, user } = useUser();
  const {
    unityData,
    unityData: { sceneName, quizNextActivity },
    updateTokens,
    updateNeedsPasswordToLogout,
    isInitialized,
    updateQuizNextActivity,
  } = useUnityDataStore();
  const { handleUnityEvents, handleLogout } = useUnityEvents();
  const [buildData, setBuildData] = useState<BuildData | null>(null);
  const [iteration, setIteration] = useState(0);
  const [userExists, setUserExists] = useState<boolean | null>(null);

  const { mutate } = useVerifyStudentAtlasPasswordMutation();

  useUrls();

  useEffect(() => {
    const tokens = storage.tokenInfo.get();
    if (isLoggedIn && user && tokens) {
      updateTokens({
        AccessToken: tokens?.access_token,
        RefreshToken: tokens?.refresh_token,
        IdToken: tokens?.id_token,
        ClientID: clientId,
      });

      const checkUserExists = async () => {
        await mutate(
          { body: { studentUuid: user!.id, password: '' } },
          {
            onSuccess() {
              setUserExists(true);
            },
            onError() {
              setUserExists(false);
              handleLogout();
            },
          }
        );
      };

      checkUserExists();

      const envVariables = {
        CONFIG_CAT_KEY: import.meta.env.VITE_CONFIG_CAT_KEY,
        DATADOG_APPLICATION_ID: import.meta.env
          .VITE_DATADOG_WEBAPP_APPLICATION_ID,
        DATADOG_CLIENT_TOKEN: import.meta.env.VITE_DATADOG_WEBAPP_CLIENT_TOKEN,
        DATADOG_ENVIRONMENT: import.meta.env.VITE_DATADOG_WEBAPP_ENVIRONMENT,
        DATADOG_SERVICE: import.meta.env.VITE_DATADOG_WEBAPP_SERVICE,
        COMMIT_ID: import.meta.env.VITE_WEBAPP_COMMIT_ID,
      };
      const sampleRates = {
        sessionReplaySampleRate: 0,
      };
      initDatadog(envVariables, sampleRates, {
        uid: user?.id,
        school: user?.organizationId,
      });

      updateNeedsPasswordToLogout(
        localStorage.getItem('sso_clicked') !== 'true'
      );
    }
  }, [isLoggedIn, user]);

  useEffect(() => {
    const setConfig = async (): Promise<void> => {
      const resolvedBuildData: BuildData = await buildResolver(sceneName);

      if (resolvedBuildData.buildPath === BUILD_NOT_FOUND) {
        //TODO
      } else {
        setIteration(iteration + 1);
        setBuildData(resolvedBuildData);
      }
    };

    setConfig();
    if (quizNextActivity) {
      updateQuizNextActivity(false);
    }
  }, [sceneName, quizNextActivity]);

  if (!isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  if (userExists === null || !isInitialized() || !buildData) {
    return <></>;
  }

  return (
    <UnityWebGL
      unityDataInput={unityData}
      buildData={buildData}
      key={buildData.buildPath + iteration}
      bridgeFunction={(eventMessage: MessageEvent) => {
        handleUnityEvents(eventMessage);
      }}
    />
  );
}

export default Home;
